import { Close } from 'generated/icons';
import cx from 'classnames';
import { merge } from 'lodash';
import React, { useMemo } from 'react';
import { default as ReactModal } from 'react-modal';
import './modal.css';

import Spinner from '../Spinner';
import Text, { textVariantClassnames } from '../Text';
import Toast from '../Toast';

export enum ModalWidths {
  FULL = 'w-full',
  XL3 = 'max-w-3xl',
  XL2 = 'max-w-2xl',
  MD = 'max-w-md',
}

export interface ModalProps {
  /** content inside modal */
  children?: React.ReactNode;
  /** additional css classes */
  className?: string;
  /** modal is visible when set to true */
  isOpen?: boolean;
  /** loading state */
  loading?: boolean;
  /**  If true (default), show a close icon in the top-right corner */
  showCloseIcon?: boolean;
  /** callback when react-modal is closed*/
  onClose?: () => void;
  /** header text */
  title?: string | React.ReactNode;
  /** subheader text */
  subtitle?: string | React.ReactNode;
  /* if true, content in the modal will be padded */
  padded?: boolean;
  /* if defined, apply this padding to the title */
  titlePadding?: string;
  /* if true, gives the modal a gray background */
  grayBackground?: boolean;
  /* max width style to use for the modal */
  width?: ModalWidths | string;
  /* If true, a horizontal line will be drawn below the header */
  showDivider?: boolean;
  /* optional styles to pass to ReactModal */
  style?: ReactModal.Styles;
  /* optional classes to apply to the header */
  headerClassName?: string;
  /* optional classes to apply to the close button */
  closeButtonClassName?: string;
  /* optional classes to apply to the modal body wrapper */
  bodyClassName?: string;
}

// rudimentary mechanism to ensure modals stack on top of each other progressively if needed
let zIndexOffset = 0;
export const getZindex = () => {
  zIndexOffset++;
  return 99999 + zIndexOffset;
};

// needed for screen readers to ignore app when react-modals are open
ReactModal.setAppElement('body');

function Modal({
  children,
  loading,
  isOpen = true,
  showCloseIcon = true,
  onClose,
  subtitle,
  title,
  padded = true,
  titlePadding,
  grayBackground,
  width,
  style = {},
  showDivider = false,
  className,
  headerClassName,
  closeButtonClassName,
  bodyClassName,
}: ModalProps) {
  const zIndex = useMemo(getZindex, [isOpen]);

  let maxWidthClass = 'max-w-xl';

  if (width) {
    maxWidthClass = width;
  }

  if (!titlePadding) {
    titlePadding = cx('py-4', padded ? 'px-8' : 'px-6');
  }

  return (
    <ReactModal
      bodyOpenClassName={null}
      className={cx(
        maxWidthClass === 'w-full' ? 'w-auto mx-4' : maxWidthClass,
        `relative inset-auto m-auto rounded min-w-2/6 focus:outline-none bg-base`,
        textVariantClassnames.base,
        className,
      )}
      isOpen={isOpen}
      onRequestClose={onClose}
      style={useMemo(
        () =>
          merge(
            {
              overlay: { backgroundColor: 'rgba(0,0,0,0.50)', zIndex },
              content: { marginTop: '10rem', marginBottom: '8rem' },
            },
            style,
          ),
        [style, zIndex],
      )}
    >
      {title && (
        <div className={cx(titlePadding, `flex justify-between flex-col`, showDivider && 'border-b')}>
          <h1 className={cx(headerClassName ?? 'text-2xl', 'mr-10 leading-loose')}>{title}</h1>
          {showCloseIcon && (
            <button
              type="button"
              aria-label="Close modal"
              onClick={onClose}
              className={cx('absolute right-4 top-4', closeButtonClassName)}
            >
              <Close className="h-[18px] w-[18px]" />
            </button>
          )}
          {subtitle && (
            <Text variant="muted" className="mb-2">
              {subtitle}
            </Text>
          )}
        </div>
      )}

      {!title && showCloseIcon && (
        <button
          type="button"
          aria-label="Close modal"
          onClick={onClose}
          className={cx('absolute right-4 top-10', closeButtonClassName)}
        >
          <Close className="h-[18px] w-[18px]" />
        </button>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="md" />
        </div>
      ) : (
        <div className={cx('rounded-b', padded && 'px-8 pb-8', grayBackground && 'bg-shade', bodyClassName)}>
          {children}
        </div>
      )}
    </ReactModal>
  );
}

interface AlertModalProps extends ModalProps {
  toastMessage?: string;
}

export function WarningModal({ toastMessage, children, ...props }: AlertModalProps) {
  return (
    <Modal
      {...props}
      className={cx('bg-alert-warning [&>div>button]:right-7 [&>div>button]:top-6', props.className)}
    >
      {toastMessage && (
        <Toast color="yellow" margin="mb-6">
          {toastMessage}
        </Toast>
      )}
      {children}
    </Modal>
  );
}

export function CriticalModal({ toastMessage, children, ...props }: AlertModalProps) {
  return (
    <Modal
      {...props}
      className={cx('bg-alert-critical [&>div>button]:right-7 [&>div>button]:top-6', props.className)}
    >
      {toastMessage && (
        <Toast color="red" margin="mb-6">
          {toastMessage}
        </Toast>
      )}
      {children}
    </Modal>
  );
}

export default React.memo(Modal);
